import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSseStreamRunner } from 'src/chat/useSseStreamRunner';
import { isErrorResult } from 'src/chat/useSSEStream';
import { AssistantMessage } from 'src/chat/AssistantMessage';
import type { ChatMessage, ActionCardData, UIAction } from 'src/chat/types';
import styles from './SsePreviewPanel.module.scss';

interface Props {
  title: string;
  sendRequest: (signal: AbortSignal) => Promise<Response>;
  onDone?: () => void;
  onClose: () => void;
}

export const SsePreviewPanel: React.FC<Props> = ({ title, sendRequest, onDone, onClose }) => {
  const { run, cancel } = useSseStreamRunner();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const msgIdRef = useRef(`preview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  // Mirrors useSSEStream's defensive paragraph-break tracker so the
  // dry-run preview doesn't render "...page 2:Excellent" collisions
  // when the server-side inter-round separator is missing (older
  // proxy / replayed run).
  const sawToolEventSinceTextRef = useRef(false);

  const startStream = useCallback(() => {
    setIsRunning(true);
    setError(null);
    const msgId = msgIdRef.current;
    const initial: ChatMessage = {
      id: msgId,
      role: 'assistant',
      content: '',
      actions: [],
      timestamp: Date.now(),
    };
    setMessages([initial]);
    sawToolEventSinceTextRef.current = false;

    run(
      (signal) => sendRequest(signal),
      {
        onTextDelta: (delta) => {
          const needsSeparator = sawToolEventSinceTextRef.current;
          sawToolEventSinceTextRef.current = false;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== msgId) return m;
              const sep =
                needsSeparator && m.content.length > 0 && !/\n\n$/.test(m.content) && !/^\n\n/.test(delta)
                  ? '\n\n'
                  : '';
              return { ...m, content: m.content + sep + delta };
            }),
          );
        },
        onToolStart: (tool, input, toolUseId) => {
          const card: ActionCardData = {
            id: `action-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            toolUseId,
            tool,
            input,
            status: 'pending',
          };
          sawToolEventSinceTextRef.current = true;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msgId ? { ...m, actions: [...(m.actions || []), card] } : m,
            ),
          );
        },
        onToolResult: (tool, result, uiAction?: UIAction, toolUseId?: string) => {
          sawToolEventSinceTextRef.current = true;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== msgId) return m;
              const actions = (m.actions || []).slice();
              // Match by tool_use_id when provided (real Claude turns +
              // updated mocks); fall back to the first pending action
              // of this tool name. Updates exactly one action per
              // result so the BatchActionCard counter increments
              // correctly during a per-asset loop.
              let idx = -1;
              if (toolUseId) {
                idx = actions.findIndex((a) => a.toolUseId === toolUseId);
              }
              if (idx === -1) {
                idx = actions.findIndex((a) => a.tool === tool && a.status === 'pending');
              }
              if (idx === -1) return m;
              actions[idx] = {
                ...actions[idx],
                // A failed call (e.g. a 403) shows as Failed, not Completed
                // (mcp-server#118).
                status: isErrorResult(result) ? 'error' : 'success',
                result,
                uiAction,
              };
              return { ...m, actions };
            }),
          );
        },
        onDone: (usage) => {
          setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, usage } : m)));
          setIsRunning(false);
          onDone?.();
        },
        onError: (code, message) => {
          setError(message);
          setIsRunning(false);
        },
      },
    );
  }, [run, sendRequest, onDone]);

  useEffect(() => {
    if (!started.current) {
      started.current = true;
      startStream();
    }
  }, [startStream]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleCancel = () => {
    cancel();
    setIsRunning(false);
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        <button className={styles.closeBtn} onClick={isRunning ? handleCancel : onClose}>
          {isRunning ? 'Cancel' : 'Close'}
        </button>
      </div>
      <div className={styles.body} ref={scrollRef}>
        {messages.map((msg) => (
          <AssistantMessage key={msg.id} message={msg} isLast />
        ))}
        {error && (
          <div className={styles.error}>
            <span>{error}</span>
            <button className={styles.retryBtn} onClick={startStream}>Try again</button>
          </div>
        )}
      </div>
      {isRunning && <div className={styles.runningIndicator}>Running…</div>}
    </div>
  );
};
